import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { CartProvider } from "@/context/CartContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { FlavourQuiz } from "@/components/FlavourQuiz";
import { MobileOrderBar } from "@/components/MobileOrderBar";
import AgeGate from "@/components/AgeGate";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import TeaserBar from "@/components/TeaserBar";
import RequireAdmin from "@/components/RequireAdmin";

import Home from "@/pages/Home";
import Verhaal from "@/pages/Verhaal";
import Shop from "@/pages/Shop";
import Contact from "@/pages/Contact";
import Checkout from "@/pages/Checkout";
import CheckoutResult from "@/pages/CheckoutResult";
import Voorwaarden from "@/pages/Voorwaarden";
import Privacy from "@/pages/Privacy";
import Unsubscribe from "@/pages/Unsubscribe";
import Review from "@/pages/Review";
import Zakelijk from "@/pages/Zakelijk";
import Merchandise from "@/pages/Merchandise";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminB2BInvoices from "@/pages/admin/AdminB2BInvoices";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminRequests from "@/pages/admin/AdminRequests";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Target may mount after async data; poll briefly.
      let attempts = 0;
      const tryScroll = () => {
        attempts += 1;
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attempts < 20) {
          setTimeout(tryScroll, 100);
        } else {
          window.scrollTo(0, 0);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const PublicLayout = () => (
  <CartProvider>
    <AgeGate />
    <TeaserBar />
    <Navbar />
    <CartDrawer />
    <main className="relative z-[2]">
      <Outlet />
    </main>
    <Footer />
    <ConsumerExtras />
  </CartProvider>
);

const HIDE_EXTRAS = ["/checkout", "/zakelijk"];

const ConsumerExtras = () => {
  const { pathname } = useLocation();
  if (HIDE_EXTRAS.some((p) => pathname.startsWith(p))) return null;
  return (
    <>
      <FlavourQuiz />
      <MobileOrderBar />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <div className="grain-overlay" />
      <HelmetProvider>
      <BrowserRouter>
        <ConfigProvider>
        <AdminAuthProvider>
          <ScrollToTop />
          <Routes>
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="requests" element={<AdminRequests />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="b2b-invoices" element={<AdminB2BInvoices />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            {/* Public */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/verhaal" element={<Verhaal />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/zakelijk" element={<Zakelijk />} />
              <Route path="/merchandise" element={<Merchandise />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/resultaat" element={<CheckoutResult />} />
              <Route path="/voorwaarden" element={<Voorwaarden />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/uitschrijven" element={<Unsubscribe />} />
              <Route path="/review/:token" element={<Review />} />
              <Route path="*" element={<Home />} />
            </Route>
          </Routes>
        </AdminAuthProvider>
        </ConfigProvider>
        <Toaster
          position="bottom-right"
          mobileOffset={{ bottom: 80 }}
          theme="dark"
          toastOptions={{
            style: {
              background: "#0a0a0a",
              border: "1px solid var(--tha-border)",
              color: "#f3f4f6",
              borderRadius: "2px",
            },
          }}
        />
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
